import React, { useEffect, useRef } from "react";
import { FormHelperText, Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import {
  HubHookCustomersSelect,
  HubIcon,
  StationHookFormSelect,
} from "../../components";
import { useGetLastShipmentBoxes } from "../../hooks/react-query/useShipmentData";

import WorkOrderTable from "./WorkOrderTable/WorkOrderTable";

import { StyledSideStack } from "./styles";
import WorkOrderRoutingTable from "./WorkOrderRoutingTable/WorkOrderRoutingTable";
import ShipmentBoxesTable from "./ShipmentBoxesTable/ShipmentBoxesTable";
import { ShipmentItemTableSkeleton } from "./Skeleton";

const AreaLabel = ({ icon, label, names = [] }) => {
  const { formState } = useFormContext();
  const { errors } = formState;
  const error = names
    .map((name) => errors[name]?.root?.message)
    .filter(Boolean);
  const message = error[0];

  return (
    <Stack direction="row" alignItems="center" gap="5px">
      {icon && <HubIcon icon={icon} size={12} />}
      <Typography variant="boldCaptionL">{label}</Typography>
      {message && (
        <FormHelperText error sx={{ marginTop: 0 }}>
          {message}
        </FormHelperText>
      )}
    </Stack>
  );
};

const weigthRed = (tol, curr) => tol + curr?.weight * curr?.quantity;
const ShipmentDetailsDialogDetails = ({
  disabled,
  options = [],
  isLoading = false,
  useStation = false,
}) => {
  const { watch, setValue } = useFormContext();
  const workOrders = watch("workOrders");
  const routes = watch("routes");
  const customerId = watch("customerId");
  const stationId = watch("stationId");
  const existingBoxes = watch("boxes");

  const prevCustomerRef = useRef(customerId);
  const prevStationRef = useRef(stationId);

  const { data: lastBoxes } = useGetLastShipmentBoxes({
    customerId,
    stationId: useStation ? stationId : null,
    returning: useStation,
  });

  useEffect(() => {
    const customerChanged = customerId !== prevCustomerRef.current;
    const stationChanged = useStation && stationId !== prevStationRef.current;

    if ((customerChanged || stationChanged) && lastBoxes?.length) {
      const hasBoxes = existingBoxes?.length > 0;
      if (!hasBoxes) {
        const weight = lastBoxes?.reduce(weigthRed, 0);
        setValue("boxes", lastBoxes);
        setValue("weight", weight);
      }
    }

    prevCustomerRef.current = customerId;
    prevStationRef.current = stationId;
  }, [customerId, stationId, lastBoxes]);

  const disableCustomer = (!useStation && workOrders?.length) || disabled;
  const disableStation = routes?.length || disabled;

  return (
    <StyledSideStack height="calc(100vh - 125px)" overflow="scroll">
      <Stack gap="10px">
        <Typography variant="boldCaptionMain">Boxing Contents</Typography>
        <Stack gap="15px">
          <HubHookCustomersSelect
            name="customerId"
            label={useStation ? "Vendor" : "Customer"}
            disabled={disableCustomer}
            rendered={false}
            required
          />
          {useStation && (
            <StationHookFormSelect
              name="stationId"
              label="Station"
              disabled={disableStation}
              useDefault={false}
              department={6}
              required
            />
          )}
        </Stack>
      </Stack>

      {isLoading ? (
        <ShipmentItemTableSkeleton />
      ) : (
        <Stack gap="10px">
          <AreaLabel
            icon="FileText"
            label="Work Orders"
            names={["workOrders", "routes"]}
          />
          {useStation ? (
            <WorkOrderRoutingTable disabled={isLoading} options={options} />
          ) : (
            <WorkOrderTable disabled={isLoading} options={options} />
          )}
        </Stack>
      )}

      <Stack gap="10px">
        <AreaLabel icon="Archive" label="Packages" names={["boxes"]} />
        <ShipmentBoxesTable />
      </Stack>
    </StyledSideStack>
  );
};

export default ShipmentDetailsDialogDetails;
